import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Popover,
  PopoverSurface,
  PopoverTrigger,
  makeStyles,
  tokens,
  mergeClasses,
} from '@fluentui/react-components';
import {
  GridDotsRegular,
  PeopleRegular,
  BookTemplateRegular,
  ChartMultipleRegular,
  type FluentIcon,
} from '@fluentui/react-icons';
import { veracityColors } from '../tokens';
import { useAuth } from '../../providers/AuthProvider';
import { hasGlobalAdmin, hasModuleAccess } from '../../auth/roles';

interface ModuleEntry {
  id: string;
  rolePrefix: string;
  label: string;
  path: string;
  icon: FluentIcon;
  color: string;
}

const MODULES: ModuleEntry[] = [
  {
    id: 'ops',
    rolePrefix: 'Ops',
    label: 'Operations',
    path: '/ops',
    icon: PeopleRegular,
    color: veracityColors.veracityOrange,
  },
  {
    id: 'catalog',
    rolePrefix: 'Catalog',
    label: 'Catalog',
    path: '/catalog',
    icon: BookTemplateRegular,
    color: veracityColors.duskBlue,
  },
  {
    id: 'sales',
    rolePrefix: 'Sales',
    label: 'Sales',
    path: '/sales',
    icon: ChartMultipleRegular,
    color: veracityColors.midnightCharcoal,
  },
];

const useStyles = makeStyles({
  trigger: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '40px',
    height: '40px',
    backgroundColor: 'transparent',
    border: 'none',
    color: 'inherit',
    cursor: 'pointer',
    borderRadius: '4px',
    ':hover': { backgroundColor: 'rgba(0,0,0,0.06)' },
  },
  triggerOnDark: {
    color: '#FFFFFF',
    ':hover': { backgroundColor: 'rgba(255,255,255,0.15)' },
  },
  panel: {
    padding: tokens.spacingVerticalM,
    width: '320px',
  },
  title: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: veracityColors.midnightCharcoal,
    marginBottom: tokens.spacingVerticalM,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: tokens.spacingVerticalS,
  },
  tile: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalS,
    borderRadius: tokens.borderRadiusMedium,
    textDecoration: 'none',
    color: veracityColors.midnightCharcoal,
    border: `1px solid ${veracityColors.firstLight}`,
    backgroundColor: '#FFFFFF',
    transitionProperty: 'background-color, transform',
    transitionDuration: '120ms',
    ':hover': {
      backgroundColor: veracityColors.firstLight,
    },
  },
  tileCurrent: {
    border: `1px solid ${veracityColors.veracityOrange}`,
    backgroundColor: veracityColors.firstLight,
    cursor: 'default',
  },
  iconCircle: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: '#FFFFFF',
    fontSize: '16px',
  },
  label: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightMedium,
  },
  empty: {
    padding: tokens.spacingVerticalL,
    fontSize: tokens.fontSizeBase200,
    color: veracityColors.duskBlue,
    textAlign: 'center',
  },
  homeLink: {
    display: 'block',
    marginTop: tokens.spacingVerticalM,
    paddingTop: tokens.spacingVerticalS,
    borderTop: `1px solid ${veracityColors.firstLight}`,
    fontSize: tokens.fontSizeBase200,
    color: veracityColors.duskBlue,
    textDecoration: 'none',
    textAlign: 'center',
    ':hover': { color: veracityColors.veracityOrange },
  },
});

interface ModuleSwitcherProps {
  /** Apply the on-dark color treatment (white icon for dark headers). */
  onDark?: boolean;
}

/**
 * Header control that lets the user jump between modules without
 * round-tripping through the launcher. Lists only modules the user has
 * access to. Highlights the module the user is currently in.
 */
export function ModuleSwitcher({ onDark }: ModuleSwitcherProps) {
  const styles = useStyles();
  const [open, setOpen] = useState(false);
  const { roles } = useAuth();
  const { pathname } = useLocation();
  const isAdmin = hasGlobalAdmin(roles);

  const accessibleModules = MODULES.filter(
    (m) => isAdmin || hasModuleAccess(roles, m.rolePrefix),
  );

  const currentModule = accessibleModules.find((m) => pathname.startsWith(m.path));

  return (
    <Popover open={open} onOpenChange={(_, data) => setOpen(data.open)} positioning="below-end">
      <PopoverTrigger>
        <button
          type="button"
          className={mergeClasses(styles.trigger, onDark && styles.triggerOnDark)}
          title="Switch module"
          aria-label="Switch module"
        >
          <GridDotsRegular style={{ fontSize: '20px' }} />
        </button>
      </PopoverTrigger>
      <PopoverSurface className={styles.panel}>
        <div className={styles.title}>Vera Modules</div>
        {accessibleModules.length === 0 ? (
          <div className={styles.empty}>
            No modules assigned. Ask a Vera admin to grant you a role.
          </div>
        ) : (
          <div className={styles.grid}>
            {accessibleModules.map((m) => {
              const Icon = m.icon;
              const isCurrent = currentModule?.id === m.id;
              return (
                <Link
                  key={m.id}
                  to={isCurrent ? '#' : m.path}
                  className={mergeClasses(styles.tile, isCurrent && styles.tileCurrent)}
                  onClick={(e) => {
                    if (isCurrent) e.preventDefault();
                    else setOpen(false);
                  }}
                >
                  <div className={styles.iconCircle} style={{ backgroundColor: m.color }}>
                    <Icon />
                  </div>
                  <span className={styles.label}>{m.label}</span>
                </Link>
              );
            })}
          </div>
        )}
        <Link to="/" className={styles.homeLink} onClick={() => setOpen(false)}>
          Home
        </Link>
      </PopoverSurface>
    </Popover>
  );
}
